export enum PROOF_OF_OWNERSHIP_STATUS {
  CREATED = "CREATED",
  VALIDATING = "VALIDATING",
  VALIDATED = "VALIDATED",
  INVALIDATED = "INVALIDATED",
  UPDATING = "UPDATING",
  UNDETERMINED = "UNDETERMINED",
  DISCARDED = "DISCARDED",
}

export enum PROOF_OF_OWNERSHIP_VERSIONS {
  FIRST = "0.0.1",
  SECOND = "0.0.2",
}

// Who currently holds a POO for validation. USER ids are user hashids (the same
// id space as loggedUser.id); SYSTEM ids are opaque names (e.g. "auto-validator").
export enum ASSIGNEE_TYPE {
  USER = "USER",
  SYSTEM = "SYSTEM",
}

// Why the analyzer sent a NEEDS_REVIEW document to the operator queue instead of
// auto-processing it. Distinct from invalidation_reason (which is a *rejection*):
// review_reason explains a hand-off. Two of these (TOO_MANY_PAGES,
// VISION_UNAVAILABLE) mean the document was never analyzed — no fields are
// extracted (confidence.overall == 0) — so the operator processes it from
// scratch. The others arrive with suggested data to validate/correct; some flag
// a field the AI couldn't read that the operator must fill in (SHARE_QUANTITY_
// NOT_FOUND → shares, DATE_NOT_FOUND → document date).
export enum REVIEW_REASON {
  TOO_MANY_PAGES = "TOO_MANY_PAGES",
  VISION_UNAVAILABLE = "VISION_UNAVAILABLE",
  LOW_CONFIDENCE = "LOW_CONFIDENCE",
  ENTITY_HOLDER = "ENTITY_HOLDER",
  ESCALATION_UNAVAILABLE = "ESCALATION_UNAVAILABLE",
  // Authenticity gate: the document was extracted but looks possibly handmade
  // (spreadsheet / screenshot / handwritten) — not confident enough to reject.
  SUSPECTED_NON_STATEMENT = "SUSPECTED_NON_STATEMENT",
  // Photo of a screen (monitor photographed) rather than a screenshot/export —
  // erases the forensic trail a real export would have.
  RECAPTURE_SUSPECTED = "RECAPTURE_SUSPECTED",
  // Source service flagged this as a suspected cross-user duplicate.
  DUPLICATE_DOCUMENT = "DUPLICATE_DOCUMENT",
  SHARE_QUANTITY_NOT_FOUND = "SHARE_QUANTITY_NOT_FOUND",
  DATE_NOT_FOUND = "DATE_NOT_FOUND",
}

// The review reasons that come back WITHOUT extracted data: the document wasn't
// processed at all, so empty fields are expected (not "nothing found"). The UI
// shows a neutral "not processed" notice + a blank manual-entry form, never an
// accept/reject suggestion.
export const NO_DATA_REVIEW_REASONS: ReadonlySet<string> = new Set<string>([
  REVIEW_REASON.TOO_MANY_PAGES,
  REVIEW_REASON.VISION_UNAVAILABLE,
])

// The review reasons where the AI extracted data but a specific field is missing
// and the operator must fill it in (shares / document date). These are NOT a
// one-click "validate": there's nothing to accept until the missing field is
// entered, so the UI shows the yellow review note and an amber-highlighted form
// (no green Accept & Apply box).
export const NEEDS_INPUT_REVIEW_REASONS: ReadonlySet<string> = new Set<string>([
  REVIEW_REASON.SHARE_QUANTITY_NOT_FOUND,
  REVIEW_REASON.DATE_NOT_FOUND,
])

// Review reasons that flag the document itself as possibly not a genuine,
// unaltered statement. Data was extracted (there's something to accept), but
// the operator must confirm authenticity before doing so — the UI shows one
// combined amber box (warning + Accept & Apply All) rather than a separate
// yellow note above a green validate box.
export const AUTHENTICITY_REVIEW_REASONS: ReadonlySet<string> = new Set<string>(
  [
    REVIEW_REASON.SUSPECTED_NON_STATEMENT,
    REVIEW_REASON.RECAPTURE_SUSPECTED,
    REVIEW_REASON.DUPLICATE_DOCUMENT,
  ],
)

// Operator-facing copy for each review_reason — the single source of truth for
// both the "not processed" notice (no-data reasons) and the inline note shown
// above the validate suggestion (data-bearing reasons). SHARE_QUANTITY_NOT_FOUND
// and DATE_NOT_FOUND name a field the operator must fill in before accepting, so
// their copy is imperative. Unknown codes fall back to REVIEW_REASON_FALLBACK_COPY
// (forward-compat with backend reasons the UI doesn't know yet).
export const REVIEW_REASON_COPY: Record<string, string> = {
  [REVIEW_REASON.TOO_MANY_PAGES]:
    "This document has too many pages to process automatically. Please review it manually.",
  [REVIEW_REASON.VISION_UNAVAILABLE]:
    "We couldn't read this scanned/image document automatically. Please process it manually.",
  [REVIEW_REASON.LOW_CONFIDENCE]:
    "We weren't fully confident in the extracted details. Please double-check the pre-filled fields.",
  [REVIEW_REASON.ENTITY_HOLDER]:
    "The account holder looks like a company or legal entity rather than an individual. Please verify.",
  [REVIEW_REASON.ESCALATION_UNAVAILABLE]:
    "We couldn't run a second automated check on this document. Please confirm the result.",
  [REVIEW_REASON.SUSPECTED_NON_STATEMENT]:
    "This may not be a genuine broker statement (it looks handmade — a spreadsheet, screenshot, or handwritten table). Confirm the document is authentic before accepting.",
  [REVIEW_REASON.RECAPTURE_SUSPECTED]:
    "This looks like a photo of a screen rather than a screenshot or exported file. Confirm the document is authentic before accepting.",
  [REVIEW_REASON.DUPLICATE_DOCUMENT]:
    "This document may have already been submitted by another user. Please check for duplicates before accepting.",
  [REVIEW_REASON.SHARE_QUANTITY_NOT_FOUND]:
    "We found holdings but couldn't read the number of shares. Please enter the shares from the document before accepting.",
  [REVIEW_REASON.DATE_NOT_FOUND]:
    "We couldn't read the document date. Please enter it before accepting — it's needed to check the document isn't outdated.",
}

export const REVIEW_REASON_FALLBACK_COPY =
  "This document needs a manual review. Please check the details."
